import axios from "axios";
import { useCallback, useEffect, useState } from "react";
import { ensureCsrfToken } from "../lib/csrf";
import { CapabilityGate } from "../access/AccessContext";
import { PERMISSIONS } from "../access/capabilities";

const label = (record) => record.legacy_state || record.status;
const TREATMENT_EVIDENCE_API = "/api/digital-risk/treatment-evidence/";
const FALLBACK_ERROR = "Treatment Evidence request could not be completed.";

const csrfRequestConfig = async () => ({
  withCredentials: true,
  headers: { "X-CSRFToken": await ensureCsrfToken() },
});

const requestError = (err) => {
  const data = err.response?.data;
  if (!data || typeof data !== "object" || Array.isArray(data)) return FALLBACK_ERROR;
  const message = [data.detail, data.error, data.message].find(value => typeof value === "string" && value.trim());
  return message ? message.trim().slice(0, 240) : FALLBACK_ERROR;
};

export default function TreatmentEvidencePanel() {
  const [records, setRecords] = useState([]);
  const [error, setError] = useState("");
  const [showDraft, setShowDraft] = useState(false);
  const [form, setForm] = useState({ treatment: "", evidence_level: "MODERATE", confidence_score: "0.5000", source_name: "", source_reference: "", notes: "" });

  const load = useCallback(async () => {
    try {
      const response = await axios.get(TREATMENT_EVIDENCE_API);
      setRecords(response.data);
      setError("");
    } catch (err) {
      setError(requestError(err));
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const act = async (id, action) => {
    try { await axios.post(`${TREATMENT_EVIDENCE_API}${id}/${action}/`, {}, await csrfRequestConfig()); await load(); }
    catch (err) { setError(requestError(err)); }
  };
  const create = async (event) => {
    event.preventDefault();
    try { await axios.post(TREATMENT_EVIDENCE_API, form, await csrfRequestConfig()); setShowDraft(false); await load(); }
    catch (err) { setError(requestError(err)); }
  };

  return <section className="rounded-xl border border-slate-200 bg-white p-5" aria-labelledby="treatment-evidence-heading">
    <div className="flex items-start justify-between gap-4">
      <div><h2 id="treatment-evidence-heading" className="font-semibold text-slate-900">Treatment Evidence</h2>
        <p className="mt-1 text-sm text-slate-500">Governed model-assumption evidence for Treatment Options. Publication admits evidence to future analysis; it does not authorize investment or prove implementation or outcomes.</p></div>
      <CapabilityGate permission={PERMISSIONS.treatmentEvidence.add}><button className="rounded bg-amber-600 px-3 py-2 text-sm font-medium text-white" onClick={() => setShowDraft(!showDraft)}>Create Draft</button></CapabilityGate>
    </div>
    {error && <p role="alert" className="mt-3 text-sm text-red-700">{String(error)}</p>}
    {showDraft && <form onSubmit={create} className="mt-4 grid gap-3 rounded-lg bg-slate-50 p-4 md:grid-cols-2">
      <div className="flex flex-col gap-1">
        <label htmlFor="treatment-evidence-treatment">Treatment Option ID</label>
        <input id="treatment-evidence-treatment" required placeholder="Treatment Option ID" value={form.treatment} onChange={e => setForm({...form, treatment: e.target.value})} />
      </div>
      <div className="flex flex-col gap-1">
        <label htmlFor="treatment-evidence-level">Evidence Level</label>
        <select id="treatment-evidence-level" value={form.evidence_level} onChange={e => setForm({...form, evidence_level: e.target.value})}><option>LOW</option><option>MODERATE</option><option>HIGH</option><option>VALIDATED</option></select>
      </div>
      <div className="flex flex-col gap-1">
        <label htmlFor="treatment-evidence-confidence">Confidence Score</label>
        <input id="treatment-evidence-confidence" required type="number" min="0" max="1" step="0.0001" value={form.confidence_score} onChange={e => setForm({...form, confidence_score: e.target.value})} />
      </div>
      <div className="flex flex-col gap-1">
        <label htmlFor="treatment-evidence-source-name">Source Name</label>
        <input id="treatment-evidence-source-name" required placeholder="Source name" value={form.source_name} onChange={e => setForm({...form, source_name: e.target.value})} />
      </div>
      <div className="flex flex-col gap-1">
        <label htmlFor="treatment-evidence-source-reference">Source Reference</label>
        <input id="treatment-evidence-source-reference" placeholder="Source reference" value={form.source_reference} onChange={e => setForm({...form, source_reference: e.target.value})} />
      </div>
      <button className="rounded bg-slate-900 px-3 py-2 text-sm text-white">Save Draft</button>
    </form>}
    <div className="mt-4 space-y-3">
      {records.map(record => <article key={record.id} className="rounded-lg border border-slate-200 p-3 text-sm">
        <div className="flex flex-wrap items-center gap-2"><strong>{record.treatment_code} — {record.source_name}</strong><span className="rounded-full bg-slate-100 px-2 py-1 text-xs">{label(record)}</span><span>v{record.version || "legacy"}</span><span>Confidence {record.confidence_score}</span></div>
        <p className="mt-1 text-slate-600">{record.source_reference || "No source reference"}</p>
        <p className="mt-1 text-xs text-slate-500">Author: {record.author || "Legacy / Unreviewed"} · Reviewer: {record.reviewer || "—"} · Publisher: {record.publisher || "—"}</p>
        <div className="mt-2 flex gap-2">
          {record.status === "DRAFT" && <CapabilityGate permission={PERMISSIONS.treatmentEvidence.submit}><button onClick={() => act(record.id, "submit")}>Submit</button></CapabilityGate>}
          {record.status === "SUBMITTED" && <CapabilityGate permission={PERMISSIONS.treatmentEvidence.review}><button onClick={() => act(record.id, "review")}>Review Evidence</button></CapabilityGate>}
          {record.status === "REVIEWED" && <CapabilityGate permission={PERMISSIONS.treatmentEvidence.publish}><button onClick={() => act(record.id, "publish")}>Publish Evidence</button></CapabilityGate>}
          {(record.status === "PUBLISHED" || record.legacy_state) && !record.successor_id && <CapabilityGate permission={PERMISSIONS.treatmentEvidence.supersede}><button onClick={() => act(record.id, "successor")}>Create successor</button></CapabilityGate>}
        </div>
      </article>)}
      {!records.length && !error && <p className="text-sm text-slate-500">No Treatment Evidence is visible.</p>}
    </div>
  </section>;
}
